import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartDataset,
  Legend,
  LinearScale,
} from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { memo } from 'react';
import { Bar } from 'react-chartjs-2';
import styled, { useTheme } from 'styled-components';

import useMedia from '~hooks/useMedia';
import { generateUUID } from '~utils/common/uuid';
import { formattedMoney, millionFormattedDeletedComma } from '~utils/formatted/number';

ChartJS.register(ChartDataLabels, CategoryScale, LinearScale, BarElement, Legend);

interface IContext extends Context {
  dataset: CustomChartDataset;
}

type CustomChartDataset = ChartDataset & {
  customData: (string | number)[];
};

interface GraphProps {
  values: number[];
  labels: string[];
}
function BarGraph({ values, labels }: GraphProps) {
  const { data, options } = GraphCalcurationData({ labels, values });

  return (
    <BarGraphWrapper id={generateUUID()}>
      {/* @ts-ignore */}
      <Bar data={data} options={options} />
    </BarGraphWrapper>
  );
}

export default memo(BarGraph);

function GraphCalcurationData({ values, labels }: GraphProps) {
  const { isDesktop } = useMedia();
  const theme = useTheme();

  const graphLabel = () => {
    // 산업군이 기타인 경우 OR  산업군이 1000개 미만인 경우 [업체 연봉, 전체 평균 연봉] 2가지 그래프만 노출
    if (labels[1] === '') {
      return [labels[0], labels[2]];
    }

    return labels;
  };

  const graphData = (): { data: string[]; customData: string[] } => {
    const formattedData: string[] = values.map((value: number) =>
      millionFormattedDeletedComma(value),
    );

    if (values[0] === 0) {
      // 선택한 기업에 연봉값이 없을경우 전체 평균(배열에 2번째 인덱스 즉)과 동일한 값으로 설정

      // 위 조건에 해당되면서 산업군이 기타인 경우 OR 산업군이 1000개 미만인 경우
      if (values[1] === 0 || labels[1].length === 0) {
        return {
          data: [formattedData[2], formattedData[2]],
          customData: ['?', formattedData[2]],
        };
      }

      return {
        data: [formattedData[2], formattedData[1], formattedData[2]],
        customData: ['?', formattedData[1], formattedData[2]],
      };
    }

    // 산업군이 기타인 경우 OR  산업군이 1000개 미만인 경우 [업체 연봉, 전체 평균 연봉] 2가지 그래프만 노출
    if (values[1] === 0 || labels[1].length === 0) {
      return {
        data: [formattedData[0], formattedData[2]],
        customData: [formattedData[0], formattedData[2]],
      };
    }

    return {
      data: formattedData,
      customData: formattedData,
    };
  };

  const maxGraphHegiht = Math.max(...graphData().data.map((str) => Number(str))) * 2;
  const options = {
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    animation: {
      duration: 0,
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        labels: {
          value: {
            color: theme.colors.gray800,
            align: 'end',
            anchor: 'end',
            font: {
              weight: '500',
              lineHeight: '12px',
              size: '10px',
              family: 'Pretendard',
            },
            formatter: (value: number, ctx: IContext) => {
              const datasets = ctx.dataset;
              if (datasets.customData !== datasets.data) {
                const label = datasets.customData[ctx.dataIndex];
                // 연봉 데이터가 수집중일때는 "?"
                if (label === '?') return datasets.customData[ctx.dataIndex];

                return formattedMoney(datasets.customData[ctx.dataIndex]);
              }

              return Math.round(value).toLocaleString();
            },
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            weight: '500',
            lineHeight: '12px',
            size: '10px',
            family: 'Pretendard',
          },
          color: theme.colors.gray700,
        },
      },
      y: {
        grid: {
          drawBorder: false,
          display: false,
        },
        ticks: {
          display: false,
        },
        max: isNaN(Number(graphData().data[0]) * 2) ? 0 : maxGraphHegiht,
      },
    },
  };

  const data = {
    labels: graphLabel(),
    datasets: [
      {
        backgroundColor: [theme.colors.magenta700, theme.colors.gray400, theme.colors.gray400],
        borderColor: [theme.colors.magenta700, theme.colors.gray400, theme.colors.gray400],
        borderWidth: 1,
        barThickness: isDesktop ? 30 : 35,
        borderRadius: 3,
        ...graphData(),
      },
    ],
  };

  return { data, options };
}

const BarGraphWrapper = styled.div`
  position: relative;
  padding-top: 10px;
  margin: 0 auto;

  width: 100%;

  canvas {
    width: 100%;
    height: 160px;
  }

  ${({ theme }) => theme.media.with('mobile', 'tablet')} {
    width: 100%;

    canvas {
      width: 100% !important;
      height: 160px !important;
    }
  }
`;
